import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name?.split(" ")[0] || "there"}`);
      if (u.must_change_password) {
        navigate("/change-password", { replace: true });
        return;
      }
      const dest = from ||
        (u.role === "admin" ? "/dashboard/admin" :
         u.role === "partner" ? "/dashboard/partner" :
         "/dashboard/customer");
      navigate(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-2 gap-16 items-center">
        <div className="hidden md:block">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-primary mb-4">Welcome back</div>
          <h1 className="font-display text-5xl font-black tracking-tighter leading-[0.95]">
            Book PUC tests.<br />
            Skip the queues.<br />
            <span className="text-primary">Breathe easier.</span>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-md leading-relaxed">
            Sign in to track your vehicle certificates, book new tests, or manage your partner centre.
          </p>
        </div>

        <div className="border border-border bg-card p-8 md:p-10">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-2">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-8">Use your VayuCheck account or continue with Google.</p>

          <Button data-testid="google-login-btn" onClick={handleGoogle} type="button" variant="outline" className="w-full rounded-lg border-border text-foreground hover:bg-secondary h-11">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#EA4335" d="M5.3 9.6c1-3 3.7-5 6.7-5 1.8 0 3.4.7 4.7 1.8L20 3c-2.1-2-4.9-3-8-3C7.4 0 3.4 2.6 1.4 6.5l3.9 3.1z"/><path fill="#34A853" d="M12 22c3.1 0 5.7-1 7.6-2.8l-3.7-2.9c-1 .7-2.3 1.1-3.9 1.1-3 0-5.5-2-6.4-4.7l-3.9 3C3.4 19.4 7.4 22 12 22z"/><path fill="#4A90E2" d="M23 12c0-.8-.1-1.5-.2-2.2H12v4.5h6.2c-.3 1.4-1.1 2.6-2.3 3.4l3.7 2.9c2.2-2 3.4-4.9 3.4-8.6z"/><path fill="#FBBC05" d="M5.6 12c0-.8.1-1.5.4-2.2L2.1 6.7C1.4 8.3 1 10.1 1 12s.4 3.7 1.1 5.3l3.9-3c-.3-.7-.4-1.5-.4-2.3z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
              <Input data-testid="login-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
              <Input data-testid="login-password" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            New here? <Link to="/register" data-testid="link-register" className="text-primary hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
